// ClientManager.jsx - Version 100% Tailwind CSS (sans style inline)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';

export default function ClientManager() {
    const { token, user, activeRole } = useAuth();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination
    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        totalPages: 0,
        totalElements: 0
    });
    
    // États pour la recherche
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modals
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [deletingClient, setDeletingClient] = useState(null);
    const [passwordClient, setPasswordClient] = useState(null);
    const [notificationClient, setNotificationClient] = useState(null);
    
    // États pour la validation des champs uniques
    const [fieldErrors, setFieldErrors] = useState({
        email: { valid: true, message: '' },
        mobile: { valid: true, message: '' },
        code: { valid: true, message: '' }
    });
    const [checkingUniqueness, setCheckingUniqueness] = useState(false);
    const checkTimeoutRef = useRef(null);
    
    // Formulaire client
    const [formData, setFormData] = useState({
        code: '',
        nomEntreprise: '',
        description: '',
        email: '',
        mobile: '',
        adresse: '',
        maxEtudiants: 100,
        licenceType: 'MONTH'
    });
    
    // Formulaire pour la création (uniquement le mot de passe et MFA)
    const [userFormData, setUserFormData] = useState({
        password: '',
        mfaEnabled: false,
        mfaType: 'NONE'
    });
    
    // Formulaire pour le mot de passe
    const [passwordData, setPasswordData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    
    // Formulaire pour la notification
    const [notificationData, setNotificationData] = useState({
        type: 'EMAIL',
        message: ''
    });
    
    // Vérifier si l'utilisateur est ADMIN ou SUPERADMIN
    const isAdmin = activeRole?.role === 'ADMIN' || activeRole?.role === 'SUPERADMIN';

    // ============================================================
    // FONCTION DE VÉRIFICATION D'UNICITÉ
    // ============================================================
    const checkUniqueness = useCallback(async (field, value, excludeId = null) => {
        if (!value || value.length < 2) {
            return { valid: true, message: '' };
        }

        setCheckingUniqueness(true);
        
        try {
            let url = `${APP_CONFIG.BASE_URL}/api/v1/admin/clients/check-unique?field=${encodeURIComponent(field)}&value=${encodeURIComponent(value)}`;
            if (excludeId) {
                url += `&excludeId=${excludeId}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (response.ok) {
                const data = await response.json();
                return { 
                    valid: data.available !== false, 
                    message: data.message || (data.available ? 'Valeur disponible' : 'Cette valeur est déjà utilisée')
                };
            } else {
                return { valid: true, message: '' };
            }
        } catch (err) {
            console.error(`Erreur vérification unicité ${field}:`, err);
            return { valid: true, message: '' };
        } finally {
            setCheckingUniqueness(false);
        }
    }, [token]);

    // ============================================================
    // VALIDATION EN TEMPS RÉEL DES CHAMPS
    // ============================================================
    const validateField = useCallback((field, value) => {
        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
        }

        if (!value || value.length < 2) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: { valid: true, message: '' }
            }));
            return;
        }

        checkTimeoutRef.current = setTimeout(async () => {
            const result = await checkUniqueness(field, value);
            setFieldErrors(prev => ({
                ...prev,
                [field]: { valid: result.valid, message: result.message }
            }));
        }, 500);
    }, [checkUniqueness]);

    useEffect(() => {
        return () => {
            if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
            }
        };
    }, []);

    // ============================================================
    // RÉCUPÉRATION DES CLIENTS
    // ============================================================
    const fetchClients = useCallback(async (page = 0) => {
        if (!token || !isAdmin) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let url = `${APP_CONFIG.BASE_URL}/api/v1/admin/clients?page=${page}&size=${pagination.size}`;
            if (searchTerm) {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (!response.ok) {
                if (response.status === 401) throw new Error('Session expirée');
                if (response.status === 403) throw new Error('Accès refusé - Vous devez être administrateur');
                throw new Error(`Erreur HTTP ${response.status}`);
            }

            const data = await response.json();
            
            if (data.status === 'SUCCESS' && data.data) {
                setClients(data.data);
                setPagination({
                    page: data.page || 0,
                    size: data.size || 10,
                    totalPages: data.totalPages || 0,
                    totalElements: data.totalElements || 0
                });
            } else if (Array.isArray(data)) {
                setClients(data);
                setPagination(prev => ({
                    ...prev,
                    page: page,
                    totalElements: data.length,
                    totalPages: Math.ceil(data.length / prev.size)
                }));
            } else {
                setClients([]);
            }
        } catch (err) {
            console.error("Erreur API ClientManager:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token, pagination.size, searchTerm, isAdmin]);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    // ============================================================
    // CRÉATION D'UN CLIENT AVEC UTILISATEUR
    // ============================================================
    const handleCreate = async (e) => {
        e.preventDefault();
        
        const hasErrors = Object.values(fieldErrors).some(f => !f.valid);
        if (hasErrors) {
            const errorMessages = Object.entries(fieldErrors)
                .filter(([, value]) => !value.valid)
                .map(([field, value]) => `${field}: ${value.message}`)
                .join('\n');
            alert(`❌ Veuillez corriger les erreurs suivantes :\n${errorMessages}`);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const payload = {
                ...formData,
                password: userFormData.password,
                mfaEnabled: userFormData.mfaEnabled,
                mfaType: userFormData.mfaType
            };

            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/clients`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                setShowModal(false);
                resetForm();
                fetchClients(pagination.page);
                alert("✅ Client créé avec succès");
            } else {
                let errorMsg = data.error || "Erreur lors de la création";
                
                if (errorMsg.includes('Duplicate entry') || errorMsg.includes('UK')) {
                    if (errorMsg.includes('mobile') || errorMsg.includes('UKg4nnlkufb5mw48txdl5gwsulf')) {
                        errorMsg = `Le numéro de téléphone "${formData.mobile}" est déjà utilisé par un autre client.`;
                        setFieldErrors(prev => ({
                            ...prev,
                            mobile: { valid: false, message: 'Ce numéro est déjà utilisé' }
                        }));
                    } else if (errorMsg.includes('email') || errorMsg.includes('UK')) {
                        errorMsg = `L'email "${formData.email}" est déjà utilisé par un autre client.`;
                        setFieldErrors(prev => ({
                            ...prev,
                            email: { valid: false, message: 'Cet email est déjà utilisé' }
                        }));
                    } else if (errorMsg.includes('code') || errorMsg.includes('UK')) {
                        errorMsg = `Le code "${formData.code}" est déjà utilisé par un autre client.`;
                        setFieldErrors(prev => ({
                            ...prev,
                            code: { valid: false, message: 'Ce code est déjà utilisé' }
                        }));
                    }
                }
                
                setError(errorMsg);
                alert(`❌ ${errorMsg}`);
            }
        } catch (err) {
            console.error("Erreur création:", err);
            setError("Erreur technique lors de la création");
            alert("❌ Erreur technique lors de la création");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // MISE À JOUR D'UN CLIENT
    // ============================================================
    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/clients/${editingClient.id}`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                setShowEditModal(false);
                setEditingClient(null);
                resetForm();
                fetchClients(pagination.page);
                alert("✅ Client modifié avec succès");
            } else {
                setError(data.error || "Erreur lors de la modification");
                alert(`❌ ${data.error || "Erreur lors de la modification"}`);
            }
        } catch (err) {
            console.error("Erreur modification:", err);
            setError("Erreur technique lors de la modification");
            alert("❌ Erreur technique lors de la modification");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // CONFIRMATION DE SUPPRESSION
    // ============================================================
    const confirmDelete = (client) => {
        setDeletingClient(client);
        setShowDeleteModal(true);
    };

    // ============================================================
    // SUPPRESSION D'UN CLIENT
    // ============================================================
    const handleDelete = async () => {
        if (!deletingClient) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/clients/${deletingClient.id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (response.ok) {
                setShowDeleteModal(false);
                setDeletingClient(null);
                fetchClients(pagination.page);
                alert("✅ Client supprimé avec succès");
            } else {
                const data = await response.json();
                setError(data.error || "Erreur lors de la suppression");
                alert(`❌ ${data.error || "Erreur lors de la suppression"}`);
            }
        } catch (err) {
            console.error("Erreur suppression:", err);
            setError("Erreur technique lors de la suppression");
            alert("❌ Erreur technique lors de la suppression");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // ACTIVATION/DÉSACTIVATION
    // ============================================================
    const handleToggleStatus = async (client) => {
        const newStatus = !client.actif;
        const action = newStatus ? 'activer' : 'désactiver';
        
        if (!window.confirm(`Êtes-vous sûr de vouloir ${action} le client ${client.nomEntreprise} ?`)) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/clients/${client.id}/status?actif=${newStatus}`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (response.ok) {
                fetchClients(pagination.page);
                alert(`✅ Client ${action} avec succès`);
            } else {
                const data = await response.json();
                setError(data.error || `Erreur lors de l'${action}`);
                alert(`❌ ${data.error || `Erreur lors de l'${action}`}`);
            }
        } catch (err) {
            console.error("Erreur changement statut:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // RÉINITIALISATION DU MOT DE PASSE
    // ============================================================
    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            alert("❌ Les mots de passe ne correspondent pas");
            return;
        }
        
        if (passwordData.newPassword.length < 6) {
            alert("❌ Le mot de passe doit contenir au moins 6 caractères");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/clients/${passwordClient.id}/reset-password`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ newPassword: passwordData.newPassword })
            });

            if (response.ok) {
                setShowPasswordModal(false);
                setPasswordClient(null);
                setPasswordData({ newPassword: '', confirmPassword: '' });
                alert("✅ Mot de passe réinitialisé avec succès");
            } else {
                const data = await response.json();
                setError(data.error || "Erreur lors de la réinitialisation");
                alert(`❌ ${data.error || "Erreur lors de la réinitialisation"}`);
            }
        } catch (err) {
            console.error("Erreur réinitialisation:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // ENVOI DE NOTIFICATION
    // ============================================================
    const handleSendNotification = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/clients/${notificationClient.id}/notification`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(notificationData)
            });

            if (response.ok) {
                setShowNotificationModal(false);
                setNotificationClient(null);
                setNotificationData({ type: 'EMAIL', message: '' });
                alert("✅ Notification envoyée avec succès");
            } else {
                const data = await response.json();
                setError(data.error || "Erreur lors de l'envoi");
                alert(`❌ ${data.error || "Erreur lors de l'envoi"}`);
            }
        } catch (err) {
            console.error("Erreur notification:", err);
            setError("Erreur technique");
            alert("❌ Erreur technique");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // OUVERTURE DES MODALS
    // ============================================================
    const openEditModal = (client) => {
        setEditingClient(client);
        setFormData({
            code: client.code || '',
            nomEntreprise: client.nomEntreprise || '',
            description: client.description || '',
            email: client.email || '',
            mobile: client.mobile || '',
            adresse: client.adresse || '',
            maxEtudiants: client.maxEtudiants || 100,
            licenceType: client.licenceType || 'MONTH'
        });
        setFieldErrors({
            email: { valid: true, message: '' },
            mobile: { valid: true, message: '' },
            code: { valid: true, message: '' }
        });
        setShowEditModal(true);
    };

    const openPasswordModal = (client) => {
        setPasswordClient(client);
        setPasswordData({ newPassword: '', confirmPassword: '' });
        setShowPasswordModal(true);
    };

    const openNotificationModal = (client) => {
        setNotificationClient(client);
        setNotificationData({ type: 'EMAIL', message: '' });
        setShowNotificationModal(true);
    };

    // ============================================================
    // RÉINITIALISATION DU FORMULAIRE
    // ============================================================
    const resetForm = () => {
        setFormData({
            code: '',
            nomEntreprise: '',
            description: '',
            email: '',
            mobile: '',
            adresse: '',
            maxEtudiants: 100,
            licenceType: 'MONTH'
        });
        setUserFormData({
            password: '',
            mfaEnabled: false,
            mfaType: 'NONE'
        });
        setFieldErrors({
            email: { valid: true, message: '' },
            mobile: { valid: true, message: '' },
            code: { valid: true, message: '' }
        });
        setError(null);
    };

    // ============================================================
    // GESTION DES CHANGEMENTS DE CHAMPS
    // ============================================================
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        if (['email', 'mobile', 'code'].includes(name)) {
            validateField(name, newValue);
        }
    };

    const handleUserInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setUserFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNotificationChange = (e) => {
        const { name, value } = e.target;
        setNotificationData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // ============================================================
    // RECHERCHE
    // ============================================================
    const handleSearch = (e) => {
        e.preventDefault();
        setSearchTerm(search);
        fetchClients(0);
    };

    // ============================================================
    // AFFICHAGE DU STATUT
    // ============================================================
    const getStatusBadge = (actif) => {
        if (actif === undefined || actif === null) return null;
        return actif ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                Actif
            </span>
        ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Inactif
            </span>
        );
    };

    // ============================================================
    // CLONAGE D'UN CLIENT
    // ============================================================
    const handleClone = async (client) => {
        const newCode = prompt("Entrez le nouveau code pour le clone:", `${client.code}_COPY`);
        const newName = prompt("Entrez le nouveau nom de l'entreprise:", `${client.nomEntreprise} (Copie)`);
        
        if (!newCode || !newName) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/admin/clients/${client.id}/clone?nouveauCode=${encodeURIComponent(newCode)}&nouveauNomEntreprise=${encodeURIComponent(newName)}`,
                {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Accept": "application/json"
                    }
                }
            );

            if (response.ok) {
                fetchClients(pagination.page);
                alert("✅ Client cloné avec succès");
            } else {
                const data = await response.json();
                setError(data.error || "Erreur lors du clonage");
                alert(`❌ ${data.error || "Erreur lors du clonage"}`);
            }
        } catch (err) {
            console.error("Erreur clonage:", err);
            setError("Erreur technique lors du clonage");
            alert("❌ Erreur technique lors du clonage");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // AFFICHAGE D'ACCÈS REFUSÉ
    // ============================================================
    if (!token || !isAdmin) {
        return (
            <div className="p-6 text-center">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
                    <p className="text-amber-700">
                        Accès réservé aux administrateurs. Veuillez vous connecter avec un compte administrateur.
                    </p>
                </div>
            </div>
        );
    }

    // ============================================================
    // RENDU PRINCIPAL
    // ============================================================
    return (
        <div className="space-y-6 p-4 md:p-6 max-w-7xl mx-auto">
            {/* En-tête */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-gray-200 pb-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                        Gestion des Clients
                    </h2>
                    <p className="text-sm text-gray-500">
                        Gérez vos clients et leurs espaces
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <input
                            type="text"
                            placeholder="Rechercher un client..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-48 md:w-64"
                        />
                        <button 
                            type="submit" 
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </button>
                    </form>
                    <button
                        onClick={() => {
                            resetForm();
                            setShowModal(true);
                        }}
                        className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Ajouter
                    </button>
                </div>
            </div>

            {/* Message d'erreur */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <strong className="text-red-800">Erreur :</strong> <span className="text-red-700">{error}</span>
                </div>
            )}

            {/* Tableau des clients */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code / Nom</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email / Téléphone</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Étudiants</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Licence</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading && clients.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-8">
                                        <div className="flex justify-center">
                                            <svg className="animate-spin h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">Chargement...</p>
                                    </td>
                                </tr>
                            ) : clients.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-8">
                                        <div className="text-center">
                                            <span className="text-4xl block mb-2">🏢</span>
                                            <p className="text-sm text-gray-400">Aucun client trouvé</p>
                                            <p className="text-xs text-gray-400 mt-1">Cliquez sur "Ajouter" pour créer votre premier client</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                clients.map(client => (
                                    <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="text-sm font-bold text-gray-900">
                                                {client.code}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {client.nomEntreprise}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-sm text-gray-700">
                                                {client.email || '-'}
                                            </div>
                                            <div className="text-xs text-gray-500 font-mono">
                                                {client.mobile || '-'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className="text-sm text-gray-700">
                                                {client.maxEtudiants || 0}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {client.licenceType || 'MONTH'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {getStatusBadge(client.actif)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-center gap-1 flex-wrap">
                                                <button
                                                    onClick={() => openEditModal(client)}
                                                    className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                    title="Modifier"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleClone(client)}
                                                    className="p-1.5 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                                    title="Cloner"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => openPasswordModal(client)}
                                                    className="p-1.5 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                                    title="Réinitialiser mot de passe"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => openNotificationModal(client)}
                                                    className="p-1.5 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                                    title="Envoyer notification"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleToggleStatus(client)}
                                                    className={`p-1.5 rounded-lg transition-colors ${
                                                        client.actif 
                                                            ? 'text-gray-600 hover:text-red-600 hover:bg-red-50' 
                                                            : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                                                    }`}
                                                    title={client.actif ? 'Désactiver' : 'Activer'}
                                                >
                                                    {client.actif ? (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => confirmDelete(client)}
                                                    className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Supprimer"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
                        <span className="text-xs text-gray-500">
                            {pagination.totalElements} client(s) - Page {pagination.page + 1}/{pagination.totalPages}
                        </span>
                        <div className="flex gap-1.5">
                            <button
                                onClick={() => fetchClients(pagination.page - 1)}
                                disabled={pagination.page === 0 || loading}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                ← Précédent
                            </button>
                            <button
                                onClick={() => fetchClients(pagination.page + 1)}
                                disabled={pagination.page + 1 >= pagination.totalPages || loading}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Suivant →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ======================================== */}
            {/* MODAL : CRÉATION D'UN CLIENT */}
            {/* ======================================== */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">✦</span>
                                Nouveau client
                            </h3>
                            <button 
                                onClick={() => { setShowModal(false); resetForm(); }} 
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleCreate} className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Code *
                                        {fieldErrors.code.valid === false && (
                                            <span className="ml-2 text-red-500 text-xs">⚠️ {fieldErrors.code.message}</span>
                                        )}
                                        {fieldErrors.code.valid && formData.code.length > 1 && (
                                            <span className="ml-2 text-emerald-500 text-xs">✓ Disponible</span>
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        name="code"
                                        placeholder="Code client"
                                        required
                                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                                            fieldErrors.code.valid === false ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        autoComplete="off"
                                    />
                                    {checkingUniqueness && (
                                        <p className="text-xs text-gray-400 mt-1">Vérification en cours...</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise *</label>
                                    <input
                                        type="text"
                                        name="nomEntreprise"
                                        placeholder="Nom de l'entreprise"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.nomEntreprise}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    name="description"
                                    placeholder="Description du client"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    rows="2"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email *
                                        {fieldErrors.email.valid === false && (
                                            <span className="ml-2 text-red-500 text-xs">⚠️ {fieldErrors.email.message}</span>
                                        )}
                                        {fieldErrors.email.valid && formData.email.length > 2 && (
                                            <span className="ml-2 text-emerald-500 text-xs">✓ Disponible</span>
                                        )}
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="contact@entreprise.com"
                                        required
                                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                                            fieldErrors.email.valid === false ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        autoComplete="email"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Email de l'administrateur du client</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Téléphone
                                        {fieldErrors.mobile.valid === false && (
                                            <span className="ml-2 text-red-500 text-xs">⚠️ {fieldErrors.mobile.message}</span>
                                        )}
                                        {fieldErrors.mobile.valid && formData.mobile.length > 2 && (
                                            <span className="ml-2 text-emerald-500 text-xs">✓ Disponible</span>
                                        )}
                                    </label>
                                    <input
                                        type="tel"
                                        name="mobile"
                                        placeholder="XX XX XX XX XX"
                                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                                            fieldErrors.mobile.valid === false ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        value={formData.mobile}
                                        onChange={handleInputChange}
                                        autoComplete="tel"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Téléphone de l'administrateur du client</p>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                                <input
                                    type="text"
                                    name="adresse"
                                    placeholder="Adresse du client"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    value={formData.adresse}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre d'étudiants max</label>
                                    <input
                                        type="number"
                                        name="maxEtudiants"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.maxEtudiants}
                                        onChange={handleInputChange}
                                        min="1"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de licence</label>
                                    <select
                                        name="licenceType"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.licenceType}
                                        onChange={handleInputChange}
                                    >
                                        <option value="MONTH">Mensuelle</option>
                                        <option value="YEAR">Annuelle</option>
                                        <option value="UNLIMITED">Illimitée</option>
                                    </select>
                                </div>
                            </div>

                            <hr className="border-gray-200" />
                            
                            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                <div className="flex items-start gap-3">
                                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <p className="text-sm font-semibold text-blue-800">
                                            👤 Informations de l'administrateur du client
                                        </p>
                                        <p className="text-xs text-blue-600 mt-1">
                                            Le <strong>nom</strong> et le <strong>prénom</strong> seront automatiquement pris de votre compte administrateur.
                                            Veuillez saisir l'email et le téléphone du futur administrateur du client.
                                        </p>
                                        {user?.nom && user?.prenom && (
                                            <p className="text-xs text-blue-700 mt-2 bg-blue-100 p-2 rounded-lg">
                                                <strong>Administrateur connecté :</strong> {user.prenom} {user.nom}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
                                <input
                                    type="password"
                                    name="password"
                                    placeholder="Mot de passe (min 6 caractères)"
                                    required
                                    minLength="6"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    value={userFormData.password}
                                    onChange={handleUserInputChange}
                                />
                                <p className="text-xs text-gray-400 mt-1">Mot de passe pour l'administrateur du client</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">MFA activé</label>
                                    <select
                                        name="mfaEnabled"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={userFormData.mfaEnabled ? 'true' : 'false'}
                                        onChange={(e) => {
                                            const val = e.target.value === 'true';
                                            setUserFormData(prev => ({ ...prev, mfaEnabled: val }));
                                        }}
                                    >
                                        <option value="false">Non</option>
                                        <option value="true">Oui</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type MFA</label>
                                    <select
                                        name="mfaType"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={userFormData.mfaType}
                                        onChange={handleUserInputChange}
                                    >
                                        <option value="NONE">Aucun</option>
                                        <option value="EMAIL">Email</option>
                                        <option value="SMS">SMS</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowModal(false);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || checkingUniqueness || Object.values(fieldErrors).some(f => !f.valid)}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? 'Création...' : 'Créer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : MODIFICATION D'UN CLIENT */}
            {/* ======================================== */}
            {showEditModal && editingClient && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">✎</span>
                                Modifier le client
                            </h3>
                            <button 
                                onClick={() => { setShowEditModal(false); setEditingClient(null); resetForm(); }} 
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleUpdate} className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                                    <input
                                        type="text"
                                        name="code"
                                        placeholder="Code client"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.code || ''}
                                        onChange={handleInputChange}
                                        autoComplete="off"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise *</label>
                                    <input
                                        type="text"
                                        name="nomEntreprise"
                                        placeholder="Nom de l'entreprise"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.nomEntreprise || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    name="description"
                                    placeholder="Description du client"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    rows="2"
                                    value={formData.description || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="contact@entreprise.com"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.email || ''}
                                        onChange={handleInputChange}
                                        autoComplete="email"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                                    <input
                                        type="tel"
                                        name="mobile"
                                        placeholder="XX XX XX XX XX"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.mobile || ''}
                                        onChange={handleInputChange}
                                        autoComplete="tel"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                                <input
                                    type="text"
                                    name="adresse"
                                    placeholder="Adresse du client"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    value={formData.adresse || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre d'étudiants max</label>
                                    <input
                                        type="number"
                                        name="maxEtudiants"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.maxEtudiants || 100}
                                        onChange={handleInputChange}
                                        min="1"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de licence</label>
                                    <select
                                        name="licenceType"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        value={formData.licenceType || 'MONTH'}
                                        onChange={handleInputChange}
                                    >
                                        <option value="MONTH">Mensuelle</option>
                                        <option value="YEAR">Annuelle</option>
                                        <option value="UNLIMITED">Illimitée</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setEditingClient(null);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? 'Mise à jour...' : 'Mettre à jour'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : CONFIRMATION DE SUPPRESSION */}
            {/* ======================================== */}
            {showDeleteModal && deletingClient && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h3 className="text-lg font-bold text-red-600 flex items-center gap-2">
                                ⚠️ Confirmation de suppression
                            </h3>
                            <button onClick={() => { setShowDeleteModal(false); setDeletingClient(null); }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                ×
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-600">
                                Êtes-vous sûr de vouloir supprimer <strong className="text-gray-900">{deletingClient.nomEntreprise}</strong> ?
                            </p>
                            <p className="text-xs text-gray-400 mt-1">Cette action est irréversible.</p>
                        </div>
                        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                            <button
                                onClick={() => { setShowDeleteModal(false); setDeletingClient(null); }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={loading}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? 'Suppression...' : 'Supprimer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : RÉINITIALISATION MOT DE PASSE */}
            {/* ======================================== */}
            {showPasswordModal && passwordClient && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">🔑</span>
                                Réinitialiser le mot de passe
                            </h3>
                            <button onClick={() => { setShowPasswordModal(false); setPasswordClient(null); }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleResetPassword} className="p-6 space-y-4">
                            <p className="text-sm text-gray-600">
                                Réinitialisation du mot de passe pour <strong>{passwordClient.nomEntreprise}</strong>
                            </p>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe *</label>
                                <input
                                    type="password"
                                    name="newPassword"
                                    placeholder="Nouveau mot de passe (min 6 caractères)"
                                    required
                                    minLength="6"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    value={passwordData.newPassword}
                                    onChange={handlePasswordChange}
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe *</label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    placeholder="Confirmer le mot de passe"
                                    required
                                    minLength="6"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    value={passwordData.confirmPassword}
                                    onChange={handlePasswordChange}
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => { setShowPasswordModal(false); setPasswordClient(null); }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? 'Réinitialisation...' : 'Réinitialiser'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : ENVOI DE NOTIFICATION */}
            {/* ======================================== */}
            {showNotificationModal && notificationClient && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">📧</span>
                                Envoyer une notification
                            </h3>
                            <button onClick={() => { setShowNotificationModal(false); setNotificationClient(null); }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleSendNotification} className="p-6 space-y-4">
                            <p className="text-sm text-gray-600">
                                Envoyer une notification à <strong>{notificationClient.nomEntreprise}</strong>
                            </p>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type de notification *</label>
                                <select
                                    name="type"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    value={notificationData.type}
                                    onChange={handleNotificationChange}
                                >
                                    <option value="EMAIL">Email</option>
                                    <option value="SMS">SMS</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                                <textarea
                                    name="message"
                                    placeholder="Votre message..."
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    rows="4"
                                    value={notificationData.message}
                                    onChange={handleNotificationChange}
                                />
                            </div>

							
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => { setShowNotificationModal(false); setNotificationClient(null); }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? 'Envoi...' : 'Envoyer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}